// Dashboard.jsx
import React, { useState, useEffect } from 'react';
import Sidebar from '../components/sidebar';
import Header from '../components/header';
import { FaCalendarAlt, FaMobile, FaWallet, FaShoppingCart, FaCheckCircle, FaClock, FaTimesCircle, FaSearch, FaArrowUp, FaArrowDown } from 'react-icons/fa';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell
} from 'recharts';
import toast, { Toaster } from 'react-hot-toast';
import { apiCall } from '../utils/api';

function Dashboard() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState('dashboard');

  // Date states
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [filterStartDate, setFilterStartDate] = useState('');
  const [filterEndDate, setFilterEndDate] = useState('');

  // Transaction data states
  const [transactionData, setTransactionData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [barGraphData, setBarGraphData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingBarGraph, setLoadingBarGraph] = useState(false);

  // Set default dates on component mount
  useEffect(() => {
    const today = new Date();
    const formattedDate = today.toISOString().split('T')[0];
    setStartDate(formattedDate);
    setEndDate(formattedDate);
    setFilterStartDate(formattedDate);
    setFilterEndDate(formattedDate);
  }, []);

  // Only fetch on initial load
  useEffect(() => {
    fetchBarGraph();
  }, []);

  useEffect(() => {
    if (startDate && endDate)
      fetchTransactionData();
  }, [startDate, endDate])

  const fetchBarGraph = async () => {
    setLoadingBarGraph(true);
    const result = await apiCall(
      "dashbook-bar-graph",
      {},
      true
    );

    if (result.status === true) {
      setBarGraphData(result.bar_graph_report || []);
    } else {
      toast.error(result.message || 'Failed to fetch bar graph data');
      setBarGraphData([]);
    }
    setLoadingBarGraph(false);
  };

  const fetchTransactionData = async () => {
    setLoading(true);

    const result = await apiCall(
      "dashbook-rech-bill-pay",
      {
        start_date: filterStartDate,
        end_date: filterEndDate
      },
      true
    );

    if (result.status === true) {
      setTransactionData(result.transaction_report || []);
      setFilteredData(result.transaction_report || []);
    } else {
      toast.error(result.message || 'Failed to fetch transaction data');
      setTransactionData([]);
      setFilteredData([]);
    }
    setLoading(false);
  };

  const handleApplyFilter = () => {
    if (startDate && endDate) {
      setFilterStartDate(startDate);
      setFilterEndDate(endDate);
      fetchTransactionData();
    }
  };

  // Calculate statistics from data based on counter
  const calculateStats = (orderType) => {
    const filtered = filteredData.filter(item => item.order_type === orderType);
    let totalAmount = 0, successAmount = 0, pendingAmount = 0, failedAmount = 0;
    let totalCounter = 0, successCounter = 0, pendingCounter = 0, failedCounter = 0;

    filtered.forEach(item => {
      const amount = parseFloat(item.amount);
      const counter = parseInt(item.counter) || 0;

      totalAmount += amount;
      totalCounter += counter;

      const status = parseInt(item.status);
      if (status === 1) {
        successAmount += amount;
        successCounter += counter;
      } else if (status === 0 || status === 3) {
        pendingAmount += amount;
        pendingCounter += counter;
      } else if (status === 2) {
        failedAmount += amount;
        failedCounter += counter;
      }
    });

    const successRate = totalCounter > 0 ? ((successCounter / totalCounter) * 100).toFixed(1) : 0;

    return {
      totalAmount: Math.round(totalAmount),
      pendingAmount: Math.round(pendingAmount),
      successAmount: Math.round(successAmount),
      failedAmount: Math.round(failedAmount),
      totalCounter: totalCounter,
      successCounter: successCounter,
      pendingCounter: pendingCounter,
      failedCounter: failedCounter,
      successRate: parseFloat(successRate)
    };
  };

  // Process bar graph data - ensure 3 bars per date (Payment, Recharge, Bill Payment)
  const processBarGraphData = () => {
    const groupedData = {};
    const dateOrder = [];

    // Group by date
    barGraphData.forEach(item => {
      const date = item.date;
      const orderType = item.order_type;
      const amount = parseFloat(item.amount);

      if (!groupedData[date]) {
        groupedData[date] = {
          date: date,
          payment: 0,
          recharge: 0,
          billPayment: 0
        };
        dateOrder.push(date);
      }

      if (orderType === '1') {
        groupedData[date].payment += amount;
      } else if (orderType === '2') {
        groupedData[date].recharge += amount;
      } else if (orderType === '3') {
        groupedData[date].billPayment += amount;
      }
    });

    // Sort dates and take last 7
    const sortedDates = dateOrder.sort((a, b) => new Date(a) - new Date(b));
    const last7Dates = sortedDates.slice(-7);

    // Return only last 7 dates
    return last7Dates.map(date => groupedData[date]);
  };

  // Get order type label
  const getOrderTypeLabel = (type) => {
    const types = {
      '1': 'Payment',
      '2': 'Recharge',
      '3': 'Bill Payment'
    };
    return types[type] || type;
  };

  // Get status label
  const getStatusLabel = (status) => {
    const statuses = {
      '0': 'Pending',
      '1': 'Success',
      '2': 'Failed',
      '3': 'Pending'
    };
    return statuses[status] || status;
  };

  const getStatusColor = (status) => {
    const colors = {
      '1': 'bg-green-100 text-green-800',
      '0': 'bg-yellow-100 text-yellow-800',
      '2': 'bg-red-100 text-red-800',
      '3': 'bg-yellow-100 text-yellow-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  // Calculate data for summary chart
  const getChartData = () => {
    const orderTypes = ['1', '2', '3'];
    const labels = ['Payment', 'Recharge', 'Bill Payment'];
    const colors = ['#8B5CF6', '#3B82F6', '#22C55E'];

    return orderTypes.map((type, index) => {
      const stats = calculateStats(type);
      return {
        name: labels[index],
        total: stats.totalAmount,
        success: stats.successAmount,
        pending: stats.pendingAmount,
        failed: stats.failedAmount,
        color: colors[index],
        successRate: stats.successRate,
        totalCounter: stats.totalCounter
      };
    });
  };

  // Pie chart data
  const getPieData = () => {
    const chartData = getChartData();
    return chartData.map(item => ({
      name: item.name,
      value: item.total
    }));
  };

  // Format currency
  const formatCurrency = (amount) => {
    if (amount >= 10000000) {
      return (amount / 10000000).toFixed(1) + ' Cr';
    } else if (amount >= 100000) {
      return (amount / 100000).toFixed(1) + ' L';
    } else if (amount >= 1000) {
      return (amount / 1000).toFixed(1) + ' K';
    }
    return '₹' + amount.toLocaleString('en-IN');
  };

  // Format number with commas
  const formatNumber = (num) => {
    return num.toLocaleString('en-IN');
  };

  // Enhanced Stat Card Component with Header, Divider, and Table Structure
  const StatCard = ({ title, stats, icon: Icon, color, orderType }) => {
    // Sample data for the status breakdown table
    const statusData = [
      {
        status: 'Success',
        count: stats.successCounter,
        amount: stats.successAmount,
        percentage: stats.totalCounter > 0 ? ((stats.successCounter / stats.totalCounter) * 100).toFixed(1) : 0,
        color: 'green'
      },
      {
        status: 'Pending',
        count: stats.pendingCounter,
        amount: stats.pendingAmount,
        percentage: stats.totalCounter > 0 ? ((stats.pendingCounter / stats.totalCounter) * 100).toFixed(1) : 0,
        color: 'blue'
      },
      {
        status: 'Failed',
        count: stats.failedCounter,
        amount: stats.failedAmount,
        percentage: stats.totalCounter > 0 ? ((stats.failedCounter / stats.totalCounter) * 100).toFixed(1) : 0,
        color: 'red'
      }
    ];

    // Calculate total transactions
    const totalTransactions = stats.successCounter + stats.pendingCounter + stats.failedCounter;

    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden hover:shadow-md transition-shadow">
        {/* Card Header */}
        <div className="px-6 pt-6 pb-4 flex items-center justify-between border-b border-gray-100">
          <div className="flex items-center gap-3">
            <div className={`p-2.5 rounded-lg ${color}`}>
              <Icon className="text-white text-xl" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
              <p className="text-sm text-gray-500">Transaction Overview</p>
            </div>
          </div>
          <div className="text-right">
            <p className="text-2xl font-bold text-gray-900">₹{formatNumber(stats.totalAmount)}</p>
            <p className="text-sm text-gray-500">Total Amount</p>
          </div>
        </div>

        {/* Card Body - Status Table */}
        <div className="p-6">
          {/* Status Table */}
          <div>
            <div className="overflow-hidden">
              <table className="min-w-full divide-y divide-gray-200">
                <thead>
                  <tr>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                    <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Count</th>
                    <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                    <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">%</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {statusData.map((item, index) => (
                    <tr key={index} className="hover:bg-gray-50 transition-colors">
                      <td className="px-3 py-2.5 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          <div className={`w-2.5 h-2.5 rounded-full bg-${item.color}-500`}></div>
                          <span className="text-sm font-medium text-gray-700">{item.status}</span>
                        </div>
                      </td>
                      <td className="px-3 py-2.5 whitespace-nowrap text-right text-sm text-gray-900">
                        {formatNumber(item.count)}
                      </td>
                      <td className="px-3 py-2.5 whitespace-nowrap text-right text-sm text-gray-900">
                        ₹{formatNumber(item.amount)}
                      </td>
                      <td className="px-3 py-2.5 whitespace-nowrap text-right">
                        <span className={`text-sm font-semibold ${item.status === 'Success' ? 'text-green-600' :
                          item.status === 'Pending' ? 'text-yellow-600' :
                            'text-red-600'
                          }`}>
                          {item.percentage}%
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="bg-gray-50">
                  <tr>
                    <td className="px-3 py-2.5 text-sm font-semibold text-gray-900">Total</td>
                    <td className="px-3 py-2.5 text-right text-sm font-bold text-gray-900">{formatNumber(totalTransactions)}</td>
                    <td className="px-3 py-2.5 text-right text-sm font-bold text-gray-900">₹{formatNumber(stats.totalAmount)}</td>
                    <td className="px-3 py-2.5 text-right text-sm font-bold text-gray-900">{stats.successRate}%</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Loading Skeleton for Bar Graph
  const BarGraphSkeleton = () => (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-8">
      <div className="flex justify-between items-center mb-6">
        <div>
          <div className="h-7 w-56 bg-gray-200 rounded animate-pulse"></div>
          <div className="h-5 w-72 bg-gray-200 rounded mt-1 animate-pulse"></div>
        </div>
        <div className="flex items-center gap-6">
          <div className="h-5 w-24 bg-gray-200 rounded animate-pulse"></div>
          <div className="h-5 w-24 bg-gray-200 rounded animate-pulse"></div>
          <div className="h-5 w-24 bg-gray-200 rounded animate-pulse"></div>
        </div>
      </div>
      <div className="h-80 flex items-end justify-center gap-6 px-4">
        {[...Array(7)].map((_, index) => (
          <div key={index} className="flex flex-col items-center gap-3">
            <div className="flex gap-2">
              <div className="w-8 bg-gray-200 rounded animate-pulse" style={{ height: `${30 + Math.random() * 50}%` }}></div>
              <div className="w-8 bg-gray-200 rounded animate-pulse" style={{ height: `${20 + Math.random() * 40}%` }}></div>
              <div className="w-8 bg-gray-200 rounded animate-pulse" style={{ height: `${25 + Math.random() * 45}%` }}></div>
            </div>
            <div className="h-5 w-12 bg-gray-200 rounded animate-pulse"></div>
          </div>
        ))}
      </div>
    </div>
  );

  // Get stats for each order type
  const rechargeStats = calculateStats('2');
  const billPaymentStats = calculateStats('3');
  const paymentStats = calculateStats('1');

  const COLORS = ['#8B5CF6', '#3B82F6', '#22C55E'];
  const BAR_COLORS = {
    payment: '#8B5CF6',
    recharge: '#3B82F6',
    billPayment: '#22C55E'
  };

  // Process bar graph data
  const processedBarData = processBarGraphData();

  return (
    <div className="bg-gray-50 font-sans antialiased min-h-screen">
      {/* Mobile Overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      <Sidebar
        currentPage={currentPage}
        setCurrentPage={setCurrentPage}
        isOpen={sidebarOpen}
        setIsOpen={setSidebarOpen}
      />

      <div className="main-content lg:ml-[72px] transition-all duration-200">
        <Header
          currentPage={currentPage}
          sidebarOpen={sidebarOpen}
          setSidebarOpen={setSidebarOpen}
        />

        <div className="p-4 sm:p-6 lg:p-8">
          <div className="mb-6 flex justify-between items-center flex-wrap gap-2">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
              <p className="text-gray-600 text-sm">View and manage dashboard</p>
            </div>
            <div className="flex flex-wrap items-center gap-2 bg-white rounded-lg shadow-sm border border-gray-200 px-4 py-2">
              <FaCalendarAlt className="text-gray-400 text-base" />
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="border-none focus:outline-none text-base w-36 bg-transparent"
              />
              <span className="text-gray-400 text-base">to</span>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="border-none focus:outline-none text-base w-36 bg-transparent"
              />
              <button
                onClick={handleApplyFilter}
                className="ml-2 px-5 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-base font-medium rounded-lg transition-colors flex items-center gap-2"
              >
                <FaSearch />
                Apply
              </button>
            </div>
          </div>

          {/* Loading State for Stats Cards */}
          {loading && (
            <div className="flex justify-center items-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
          )}

          {/* Stats Cards - Enhanced with Header, Divider, and Table */}
          {!loading && (
            <>
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
                <StatCard
                  title="Recharge"
                  stats={rechargeStats}
                  icon={FaMobile}
                  color="bg-blue-600"
                  orderType="2"
                />
                <StatCard
                  title="Bill Payment"
                  stats={billPaymentStats}
                  icon={FaShoppingCart}
                  color="bg-green-600"
                  orderType="3"
                />
                <StatCard
                  title="Payment"
                  stats={paymentStats}
                  icon={FaWallet}
                  color="bg-purple-600"
                  orderType="1"
                />
              </div>
            </>
          )}

          {/* Bar Graph Section with Loading Skeleton - 3*7 Bars */}
          {loadingBarGraph ? (
            <BarGraphSkeleton />
          ) : (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-8">
              <div className="flex justify-between items-center mb-6">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">Daily Transaction Report</h2>
                  <p className="text-base text-gray-500">Last 7 days breakdown by transaction type</p>
                </div>
                <div className="flex items-center gap-6 text-base">
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 bg-purple-600 rounded"></div>
                    <span className="font-medium">Payment</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 bg-blue-600 rounded"></div>
                    <span className="font-medium">Recharge</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 bg-green-600 rounded"></div>
                    <span className="font-medium">Bill Payment</span>
                  </div>
                </div>
              </div>
              <div className="h-96">
                {processedBarData.length === 0 ? (
                  <div className="flex items-center justify-center h-full text-gray-500 text-lg">
                    No data available for the selected period
                  </div>
                ) : (
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={processedBarData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                      <XAxis
                        dataKey="date"
                        stroke="#9CA3AF"
                        tick={{ fontSize: 14 }}
                        tickFormatter={(date) => {
                          const d = new Date(date);
                          return `${d.getDate()}/${d.getMonth() + 1}`;
                        }}
                      />
                      <YAxis
                        stroke="#9CA3AF"
                        tick={{ fontSize: 14 }}
                        tickFormatter={(value) => formatCurrency(value)}
                      />
                      <Tooltip
                        formatter={(value) => `₹${formatNumber(value)}`}
                        labelFormatter={(label) => `Date: ${label}`}
                        contentStyle={{
                          backgroundColor: 'white',
                          border: '1px solid #E5E7EB',
                          borderRadius: '8px',
                          padding: '12px',
                          fontSize: '14px'
                        }}
                      />
                      <Legend wrapperStyle={{ fontSize: '14px', paddingTop: '10px' }} />
                      <Bar dataKey="payment" name="Payment" fill={BAR_COLORS.payment} barSize={40} />
                      <Bar dataKey="recharge" name="Recharge" fill={BAR_COLORS.recharge} barSize={40} />
                      <Bar dataKey="billPayment" name="Bill Payment" fill={BAR_COLORS.billPayment} barSize={40} />
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
      <Toaster position="top-right" />
    </div>
  );
}

export default Dashboard;